import React from 'react';
import {
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGridProps,
  TooltipProps,
  XAxisProps,
  YAxisProps,
  ReferenceLine,
  ReferenceLineProps,
} from 'recharts';
import { mergeWithCartesianDefaults } from '../chart-config/defaults';
import {
  defaultFormatters,
  FormatterFunctions,
} from '../chart-config/formatters';
import { useIsDarkMode } from '../../../../hooks/useIsDarkMode';

import {
  StringKeyOf,
  ChartType,
  RechartsValueType,
  RechartsNameType,
} from '../types';

export interface UseChartElementsProps<TData> {
  categoryKey: StringKeyOf<TData>;
  hideAllChartElements?: boolean;
  showCartesianGrid?: boolean;
  showXAxis?: boolean;
  showYAxis?: boolean;
  showTooltip?: boolean;
  showLegend?: boolean;
  formatters?: Partial<FormatterFunctions>;
  cartesianGridProps?: CartesianGridProps;
  xAxisProps?: XAxisProps;
  yAxisProps?: YAxisProps;
  tooltipProps?: TooltipProps<RechartsValueType, RechartsNameType>;
  legendProps?: React.ComponentPropsWithoutRef<typeof Legend>;
  data: TData[];
  chartType: ChartType;
  activeIndex: number | null;
  setActiveIndex: (index: number | null) => void;
  referenceLines?: ReferenceLineProps[];
}

export const useCommonChartElements = <TData,>({
  hideAllChartElements = false,
  showCartesianGrid,
  showXAxis,
  showYAxis,
  showTooltip,
  showLegend,
  categoryKey,
  cartesianGridProps,
  xAxisProps,
  yAxisProps,
  tooltipProps,
  formatters = defaultFormatters,
  legendProps,
  chartType,
  setActiveIndex,
  referenceLines,
}: UseChartElementsProps<TData>): React.ReactNode[] => {
  // Track dark mode reactively so chart colors update when theme changes
  const isDarkMode = useIsDarkMode();

  return React.useMemo(() => {
    const elements: React.ReactNode[] = [];

    const mergedFormatters = {
      ...defaultFormatters,
      ...formatters,
    };

    if (chartType !== 'pie') {
      // Pie charts don't have x and y axes
      const mergedXAxisProps = mergeWithCartesianDefaults('xAxis', {
        tickFormatter: mergedFormatters.xAxisFormatter,
        ...xAxisProps,
      });
      const mergedYAxisProps = mergeWithCartesianDefaults('yAxis', {
        tickFormatter: mergedFormatters.yAxisFormatter,
        ...yAxisProps,
      });

      elements.push(
        <XAxis
          key="xAxis"
          dataKey={categoryKey}
          hide={!showXAxis || hideAllChartElements}
          {...mergedXAxisProps}
        />,
      );

      elements.push(
        <YAxis
          key="yAxis"
          hide={!showYAxis || hideAllChartElements}
          {...mergedYAxisProps}
        />,
      );
    }

    if (hideAllChartElements) {
      return elements;
    }

    if (showTooltip) {
      const mergedTooltipProps = mergeWithCartesianDefaults('tooltip', {
        formatter: mergedFormatters.tooltipFormatter as any,
        labelFormatter: mergedFormatters.tooltipLabelFormatter as any,
        ...tooltipProps,
      });
      elements.push(<Tooltip key="tooltip" {...(mergedTooltipProps as any)} />);
    }

    if (showLegend) {
      const mergedLegendProps = mergeWithCartesianDefaults('legend', {
        formatter: mergedFormatters.legendFormatter,
        ...legendProps,
      });
      elements.push(
        <Legend
          key="legend"
          {...mergedLegendProps}
          onMouseEnter={(_, index) => {
            setActiveIndex(index);
          }}
          onMouseLeave={() => {
            setActiveIndex(null);
          }}
        />,
      );
    }

    if (chartType === 'pie') {
      return elements;
    }

    if (showCartesianGrid) {
      const mergedGridProps = mergeWithCartesianDefaults(
        'cartesianGrid',
        cartesianGridProps,
      );
      elements.push(
        <CartesianGrid
          key="grid"
          {...mergedGridProps}
          onMouseLeave={() => {
            setActiveIndex(null);
          }}
        />,
      );
    }

    if (referenceLines && referenceLines.length > 0) {
      referenceLines.forEach((lineProps, index) => {
        const mergedReferenceLineProps = mergeWithCartesianDefaults(
          'referenceLine',
          lineProps,
        );

        elements.push(
          <ReferenceLine
            key={`referenceLine-${index}`}
            {...mergedReferenceLineProps}
          />,
        );
      });
    }

    return elements;
  }, [
    hideAllChartElements,
    showCartesianGrid,
    showXAxis,
    showYAxis,
    showTooltip,
    showLegend,
    categoryKey,
    formatters,
    cartesianGridProps,
    xAxisProps,
    yAxisProps,
    tooltipProps,
    legendProps,
    chartType,
    setActiveIndex,
    isDarkMode,
    referenceLines,
  ]);
};
